import {
  Component,
  Inject,
  Input,
  OnInit,
  Optional,
  SimpleChanges,
  OnDestroy 
} from '@angular/core';
import {
  FormGroup,
  FormArray,
  UntypedFormBuilder,
  Validators,
  ValidatorFn,
  AbstractControl,
  FormsModule,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserAdminService } from '@Services';
import UserUnavailabilitySchedule from '@Models/global/user-unavailability-schedule';
import { Observable } from 'rxjs';
import { UserAvailabilityService } from '@Services/user-availability.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { isMoment } from 'moment';
import { AppService } from '@App/app.service';
import { SharedModule } from '@Shared/shared.module';
import { DateTime } from 'luxon';
import {
  NgxMatDatepickerActions,
  NgxMatDatepickerApply,
  NgxMatDatepickerCancel,
  NgxMatDatepickerClear,
  NgxMatDatepickerInput,
  NgxMatDatetimepicker,
} from '@ngxmc/datetime-picker';
import { CalendarViewComponent } from '../calendar-view/calendar-view.component';
@Component({
  standalone: true,
  selector: 'app-unavailability-schedule',
  templateUrl: './unavailability-schedule.component.html',
  styleUrls: ['./unavailability-schedule.component.scss'],
  imports: [
    SharedModule, 
    CalendarViewComponent,
    FormsModule,
    NgxMatDatepickerActions,
    NgxMatDatepickerActions,
    NgxMatDatepickerApply,
    NgxMatDatepickerCancel,
    NgxMatDatepickerClear,
    NgxMatDatepickerInput,
    NgxMatDatetimepicker,
  ],
})
export class UnavailabilityScheduleComponent implements OnInit, OnDestroy {
  @Input() selectedUser: any;
  
  today: Date;
  userSchedule: UserUnavailabilitySchedule[] = [];
  scheduleForm!: FormGroup;
  availability$!: Observable<boolean>;
  hasAvailabilityTogglePermission: boolean = false;

  constructor(
    private readonly userAdminService: UserAdminService,
    private readonly availabilityService: UserAvailabilityService,
    private readonly appService: AppService,
    private _fb: UntypedFormBuilder,
    @Optional() public scheduleDialogRef: MatDialogRef<UnavailabilityScheduleComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: any
  ) {}

  ngOnInit(): void {
    this.scheduleForm = this._fb.group({
      schedules: this._fb.array([])
    });

    this.today = new Date();
    if (this.selectedUser) {
      this.getUserAvailability();
      this.loadUnavailabilitySchedule();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.hasAvailabilityTogglePermission = this.appService.hasViewPermission('Availability Toggle');
    if (changes['selectedUser']?.currentValue && !changes['selectedUser'].firstChange) {
      this.loadUnavailabilitySchedule();
    }
  }

  ngOnDestroy(): void {}

  toggleUserAvailability(event: MatSlideToggleChange) {
    const isAvailable = event.checked;
    event.source.checked = !isAvailable;
    this.availabilityService.updateAvailability(this.selectedUser.id, event.checked, true).subscribe((response) => {});
  }
  
  getUserAvailability() {
      this.availability$ = this.availabilityService.getAvailability(this.selectedUser.id);
  }

  loadUnavailabilitySchedule(): void {
    this.userAdminService.getUserUnavailabilitySchedule(this.selectedUser?.id || '1', true).subscribe((response) => {
      if (response) {
        this.userSchedule = response;
        this.userSchedule.forEach(schedule => this.addSchedule(schedule));
      }
    });
  }

  get schedules(): FormArray {
    return this.scheduleForm.get('schedules') as FormArray;
  }

  addSchedule(schedule: any = null): void {
    const group = this._fb.group({
      id: [schedule?.id || null],
      userId: [schedule?.userId || this.selectedUser?.id || '1', Validators.required],
      startTime: [schedule?.formattedStartTime || null, Validators.required],
      endTime: [schedule?.formattedEndTime || null, Validators.required],
      isActive: [schedule?.isActive ?? true, Validators.required]
    }, {
      validators: [
        startTimeNotInPastIfIdNullValidator('startTime', 'id'),
        endTimeAfterStartTimeValidator('startTime', 'endTime'),
      ]
    });

    if (schedule) {
      group['originalValue'] = JSON.parse(JSON.stringify(group.value));
    }

    this.schedules.push(group);
  }

  getMinimum(group: AbstractControl): Date {
    if (!group.get('id')?.value) {
        return this.today;
    }
    else {
        const startTime = group.get('startTime')?.value;
        return startTime ? new Date(startTime) : this.today;
    }
  }

  onDateChanged(type: 'start' | 'end', index: number, value: DateTime): void {
    const scheduleGroup = this.schedules.at(index) as FormGroup;

    if (type === 'start') {
      scheduleGroup.get('startTime')?.setValue(value);
    } else if (type === 'end') {
      scheduleGroup.get('endTime')?.setValue(value);
    }
    this.scheduleForm.markAsDirty();
  }

  removeSchedule(index: number): void {
    const scheduleControl = this.schedules.at(index);
    scheduleControl.patchValue({ isActive: false });
    this.scheduleForm.markAsDirty();
  }

  saveSchedule(): void {
    const payload = this.schedules.controls
      .filter(ctrl => {

        const currentValue = ctrl.value;
        const originalValue = ctrl['originalValue'];

        if (!originalValue) {
          return true;
        }
        
        return Object.keys(currentValue).some(key => currentValue[key] !== originalValue[key]);
      })
      .map(ctrl => ({
        id: ctrl.get('id')?.value,
        userId: ctrl.get('userId')?.value,
        startTime: isMoment(ctrl.get('startTime').value) ?  ctrl.get('startTime').value.format('MM/DD/YYYY HH:mm:ss A') : ctrl.get('startTime').value.toLocaleString() ,
        endTime: isMoment(ctrl.get('endTime').value) ? ctrl.get('endTime').value.format('MM/DD/YYYY HH:mm:ss A') : ctrl.get('endTime').value.toLocaleString() ,
        isActive: ctrl.get('isActive')?.value,
      }));
    
    this.userAdminService.upsertUserUnavailabilitySchedule(this.selectedUser?.id || '1' , payload).subscribe({
      next: () => {
        this.schedules.clear();
        this.loadUnavailabilitySchedule();
        this.scheduleForm.markAsPristine();
      },
      error: (err) => {
        console.error('Error saving schedule:', err);
      }
    });
  }
}

export function endTimeAfterStartTimeValidator(startKey: string, endKey: string): ValidatorFn {
  return (group: AbstractControl): { [key: string]: any } | null => {
    const start = group.get(startKey)?.value;
    const end = group.get(endKey)?.value;

    if (!start || !end) return null;

    if (new Date(end) < new Date(start)) {
      group.get(endKey)?.setErrors({ endBeforeStart: true });
      return { endBeforeStart: true };
    } else {
      const endControl = group.get(endKey);
      if (endControl?.hasError('endBeforeStart')) {
        const errors = { ...endControl.errors };
        delete errors['endBeforeStart'];
        endControl.setErrors(Object.keys(errors).length ? errors : null);
      }
    }

    return null;
  };
}

export function startTimeNotInPastIfIdNullValidator(startKey: string, idKey: string): ValidatorFn {
  return (group: AbstractControl): { [key: string]: any } | null => {
    const start = group.get(startKey)?.value;
    const id = group.get(idKey)?.value;

    if (!start || id) return null;

    const now = new Date();
    const selectedStart = new Date(start);
    const startControl = group.get(startKey);

    if (selectedStart < now) {
      startControl?.setErrors({ pastDateTime: true });
      return { pastDateTime: true };
    } else {
      if (startControl?.hasError('pastDateTime')) {
        const errors = { ...startControl.errors };
        delete errors['pastDateTime'];
        startControl.setErrors(Object.keys(errors).length ? errors : null);
      }
    }

    return null;
  };
}